import { MaskedInput } from 'antd-mask-input';
import { MaskedInputProps } from 'antd-mask-input/build/main/lib/MaskedInput';
import { useEffect, useRef, useState } from 'react';

const applyMaskToDigits = (
  value: string | undefined,
  mask: MaskedInputProps['mask']
) => {
  if (!value) return value;
  if (typeof mask !== 'string') return value;

  const digits = value.replace(/\D/g, '');

  let result = '';
  let digitIndex = 0;

  for (let i = 0; i < mask.length && digitIndex < digits.length; i += 1) {
    const maskChar = mask[i];

    if (maskChar === '0') {
      result += digits[digitIndex];
      digitIndex += 1;
    } else {
      result += maskChar;
    }
  }

  return result;
};

export const InputMask = (props: MaskedInputProps) => {
  const { value, onChange, mask, maskOptions, ...rest } = props;

  // Estado interno para controlar o valor exibido
  const [internalValue, setInternalValue] = useState<string | undefined>(() => {
    if (typeof value === 'string' || typeof value === 'number') {
      return applyMaskToDigits(String(value), mask);
    }
    return undefined;
  });

  // Key para forçar remount do MaskedInput quando valor externo muda
  const [inputKey, setInputKey] = useState(0);

  // Ref para rastrear o valor anterior
  const prevValueRef = useRef<string | undefined>(undefined);

  // Ref para o onChange (evita problemas de closure)
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Quando o value externo muda (ex: form.setFieldsValue), atualiza o estado interno
  // E também atualiza o valor no Form do Ant Design com a máscara aplicada
  useEffect(() => {
    const currentValue =
      typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : undefined;

    // Só atualiza se o valor realmente mudou e não veio do próprio input
    if (currentValue !== prevValueRef.current) {
      const masked = currentValue
        ? applyMaskToDigits(currentValue, mask)
        : undefined;

      // Se o valor externo não está mascarado, precisamos forçar update
      if (masked && masked !== currentValue) {
        setInternalValue(masked);
        setInputKey(k => k + 1); // Força remount do MaskedInput

        // Atualiza o valor no Form do Ant Design com a máscara
        // Usa queueMicrotask para evitar problemas de timing
        queueMicrotask(() => {
          if (onChangeRef.current && masked) {
            onChangeRef.current({
              target: { value: masked }
            } as any);
          }
        });
      } else if (masked !== internalValue) {
        setInternalValue(masked);
      }

      prevValueRef.current = masked;
    }
  }, [value, mask]);

  const handleChange: MaskedInputProps['onChange'] = e => {
    const newValue = e.target.value;
    setInternalValue(newValue);

    // Only propagate to form when value actually changed
    // This skips the echo from MaskedInput remount while letting real user input through
    if (newValue !== prevValueRef.current && onChange) {
      onChange(e);
    }

    prevValueRef.current = newValue;
  };

  return (
    <MaskedInput
      key={inputKey}
      {...rest}
      mask={mask}
      value={internalValue}
      onChange={handleChange}
      maskOptions={{ lazy: true, ...(maskOptions || {}) }}
    />
  );
};
